import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RequestServiceResponse } from '@tcc-online/request-service';
import type { AppState } from '../../reducers/root.reducer';
import IBaseState from '../../types/IBaseState';
import IPagination from '../../types/IPagination';
import IPaginationState from '../../types/state/IPaginationState';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import AdminProjectStatisticsService, {
    GetStatisticsPayload,
} from '../services/AdminProjectStatisticsService';
import Group from '../types/project/360/Group';
import type { ParticipantProgressItem } from '../types/project/statistics/ParticipantProgress';
import type {
    ProjectStatisticsById,
    ProjectStatisticsId,
    ResponseRateStatistics,
} from '../types/project/statistics/ProjectStatistics';

const NAME = 'AdminProjectStatistics';

const getStatistics = createNotifiedThunk<
    RequestServiceResponse<ProjectStatisticsData>,
    GetStatisticsPayload<ProjectStatisticsId>
>(`${NAME}/getStatistics`, AdminProjectStatisticsService.getStatistics, {
    error: 'fe.admin.error.fetch-statistics',
});

const getParticipantsProgress = createNotifiedThunk(
    `${NAME}/getParticipantsProgress`,
    AdminProjectStatisticsService.getParticipantsProgress,
    { error: 'fe.admin.error.fetch-participant-progress' },
);

const get360Groups = createNotifiedThunk(
    `${NAME}/get360Groups`,
    AdminProjectStatisticsService.get360Groups,
    { error: 'fe.admin.error.fetch-360-groups' },
);

const getResponseRate = createNotifiedThunk<
    RequestServiceResponse<ResponseRateStatistics>,
    GetStatisticsPayload<'response-rate'>
>(`${NAME}/getResponseRate`, AdminProjectStatisticsService.getStatistics, {
    error: 'fe.admin.error.fetch-statistics',
});

export type ProjectStatisticsData = ProjectStatisticsById[ProjectStatisticsId];

export type AdminProjectStatisticsState = {
    statistics: IBaseState<ProjectStatisticsData | null>;
    responseRate: IBaseState<ResponseRateStatistics | null>;
    participantsProgress: IPaginationState<ParticipantProgressItem[]>;
    groups360: IBaseState<Group[]>;
};

const initialState: AdminProjectStatisticsState = {
    statistics: { data: null, loading: false },
    responseRate: { data: null, loading: false },
    participantsProgress: { data: [], loading: false, pagination: { page: 1 } },
    groups360: { data: [], loading: false },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getStatistics.pending,
            (state: AdminProjectStatisticsState): void => {
                state.statistics.loading = true;
                state.statistics.data = null;
            },
        );
        builder.addCase(
            getStatistics.fulfilled,
            (
                state: AdminProjectStatisticsState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectStatisticsData>
                >,
            ): void => {
                state.statistics.data = action.payload.data;
            },
        );

        builder.addCase(
            getParticipantsProgress.pending,
            (state: AdminProjectStatisticsState): void => {
                state.participantsProgress.loading = true;
                state.participantsProgress.data = [];
            },
        );
        builder.addCase(
            getParticipantsProgress.fulfilled,
            (
                state: AdminProjectStatisticsState,
                action: PayloadAction<
                    RequestServiceResponse<ParticipantProgressItem[]> & {
                        pagination?: IPagination;
                    }
                >,
            ): void => {
                state.participantsProgress.data = action.payload.data;

                if (action.payload.pagination) {
                    state.participantsProgress.pagination =
                        action.payload.pagination;
                }
            },
        );

        builder.addCase(
            get360Groups.pending,
            (state: AdminProjectStatisticsState): void => {
                state.groups360.loading = true;
                state.groups360.data = [];
            },
        );
        builder.addCase(
            get360Groups.fulfilled,
            (
                state: AdminProjectStatisticsState,
                action: PayloadAction<RequestServiceResponse<Group[]>>,
            ): void => {
                state.groups360.data = action.payload.data;
            },
        );

        builder.addCase(
            getResponseRate.pending,
            (state: AdminProjectStatisticsState): void => {
                state.responseRate.loading = true;
                state.responseRate.data = null;
            },
        );
        builder.addCase(
            getResponseRate.fulfilled,
            (
                state: AdminProjectStatisticsState,
                action: PayloadAction<
                    RequestServiceResponse<ResponseRateStatistics>
                >,
            ): void => {
                state.responseRate.data = action.payload.data;
            },
        );

        builder.addMatcher(
            isAnyOf(getStatistics.fulfilled, getStatistics.rejected),
            (state: AdminProjectStatisticsState): void => {
                state.statistics.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getParticipantsProgress.fulfilled,
                getParticipantsProgress.rejected,
            ),
            (state: AdminProjectStatisticsState): void => {
                state.participantsProgress.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(get360Groups.fulfilled, get360Groups.rejected),
            (state: AdminProjectStatisticsState): void => {
                state.groups360.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getResponseRate.fulfilled, getResponseRate.rejected),
            (state: AdminProjectStatisticsState): void => {
                state.responseRate.loading = false;
            },
        );
    },
});

const state = (state: AppState): AdminProjectStatisticsState =>
    state.admin.projectStatistics;

const AdminProjectStatisticsSlice = {
    reducer: slice.reducer,
    actions: {
        getStatistics,
        getResponseRate,
        getParticipantsProgress,
        get360Groups,
    },
    state: {
        all: state,
        getStatistics: createSelector(
            state,
            (state: AdminProjectStatisticsState) => state.statistics,
        ),
        getResponseRate: createSelector(
            state,
            (state: AdminProjectStatisticsState) => state.responseRate,
        ),
        getParticipantsProgress: createSelector(
            state,
            (state: AdminProjectStatisticsState) => state.participantsProgress,
        ),
        get360Groups: createSelector(
            state,
            (state: AdminProjectStatisticsState) => state.groups360,
        ),
    },
};

export default AdminProjectStatisticsSlice;
